import { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageLoader } from "@/components/ui/full-page-loader";
import { PageHeader } from "@/components/dashboard/shared/PageHeader";
import { DashboardHead } from "@/components/SEO/DashboardHead";
import api from "@/utils/api";
import { useCurrentStore } from "@/hooks/useCurrentStore";
import { Invoice, InvoiceItem, InvoiceSettings, InvoiceTemplate, ProductSuggestion } from "@/types/invoice";
import { RiAddLine, RiDeleteBin6Line, RiSearchLine, RiUser3Line } from "@remixicon/react";

const TEMPLATES: { value: InvoiceTemplate; label: string }[] = [
  { value: "classic", label: "Classic" },
  { value: "modern", label: "Modern" },
  { value: "minimal", label: "Minimal" },
  { value: "professional", label: "Professional" },
  { value: "bold", label: "Bold" },
  { value: "elegant", label: "Elegant" },
  { value: "formal", label: "Formal" },
  { value: "compact", label: "Compact" },
  { value: "corporate", label: "Corporate" },
  { value: "simple", label: "Simple" },
];

type ItemRow = InvoiceItem & { _key: string };

function newItem(): ItemRow {
  return { _key: Math.random().toString(36).slice(2), itemName: "", unitPrice: 0, quantity: 1, discount: 0 };
}

function lineTotal(item: ItemRow) {
  const base = item.unitPrice * item.quantity;
  return base - base * ((item.discount || 0) / 100);
}

// ── Customer suggestion type ──────────────────────────────────────────────────
interface CustomerSuggestion {
  _id: string;
  name: string;
  email: string;
  phone?: string;
  address?: string;
}

// ── Customer search autocomplete ─────────────────────────────────────────────
interface CustomerAutocompleteProps {
  onSelect: (c: CustomerSuggestion) => void;
  storeId: string;
}

function CustomerAutocomplete({ onSelect, storeId }: CustomerAutocompleteProps) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const { data: suggestions = [], isFetching } = useQuery<{ customers: CustomerSuggestion[] }>({
    queryKey: ["customer-search-invoice", storeId, q],
    queryFn: async () => {
      const { data } = await api.get("/api/customers", { params: { search: q, limit: 8 } });
      return data;
    },
    enabled: !!q.trim() && !!storeId,
  });

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div ref={ref} className="relative">
      <div className="relative">
        <RiUser3Line className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <Input
          placeholder="Search your customers to auto-fill…"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => setOpen(true), 300);
          }}
          onFocus={() => q && setOpen(true)}
          className="pl-8 h-8 text-sm"
        />
      </div>
      {open && q && (
        <div className="absolute z-50 top-full mt-1 w-full bg-white border rounded-lg shadow-lg max-h-52 overflow-y-auto">
          {isFetching ? (
            <div className="p-3 text-sm text-muted-foreground">Searching…</div>
          ) : !suggestions.customers?.length ? (
            <div className="p-3 text-sm text-muted-foreground">No customers found</div>
          ) : (
            suggestions.customers.map((c) => (
              <button
                key={c._id}
                type="button"
                onClick={() => {
                  onSelect(c);
                  setQ("");
                  setOpen(false);
                }}
                className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-muted text-left text-sm"
              >
                <div className="w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 text-xs font-bold">
                  {c.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="font-medium">{c.name}</div>
                  <div className="text-xs text-muted-foreground">{c.email}</div>
                </div>
                {c.phone && (
                  <div className="ml-auto text-xs text-muted-foreground">{c.phone}</div>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ── Product search autocomplete ───────────────────────────────────────────────
interface ProductDropdownProps {
  onSelect: (p: ProductSuggestion) => void;
  storeId: string;
}

function ProductAutocomplete({ onSelect, storeId }: ProductDropdownProps) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const { data: suggestions = [], isFetching } = useQuery<ProductSuggestion[]>({
    queryKey: ["product-search", storeId, q],
    queryFn: async () => {
      if (!q.trim()) return [];
      const { data } = await api.get("/api/invoices/products/search", { params: { q } });
      return data;
    },
    enabled: !!q.trim() && !!storeId,
  });

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div ref={ref} className="relative">
      <div className="relative">
        <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <Input
          placeholder="Search products to add as line item…"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => setOpen(true), 300);
          }}
          onFocus={() => q && setOpen(true)}
          className="pl-8 h-8 text-sm"
        />
      </div>
      {open && q && (
        <div className="absolute z-50 top-full mt-1 w-full bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {isFetching ? (
            <div className="p-3 text-sm text-muted-foreground">Searching…</div>
          ) : suggestions.length === 0 ? (
            <div className="p-3 text-sm text-muted-foreground">No products found</div>
          ) : (
            suggestions.map((p) => (
              <button
                key={p._id}
                type="button"
                onClick={() => {
                  onSelect(p);
                  setQ("");
                  setOpen(false);
                }}
                className="w-full flex items-center gap-3 px-3 py-2 hover:bg-muted text-left text-sm"
              >
                {p.image && <img src={p.image} alt="" className="w-7 h-7 rounded object-cover shrink-0" />}
                <div>
                  <div className="font-medium">{p.name}</div>
                  {p.sku && <div className="text-xs text-muted-foreground">SKU: {p.sku}</div>}
                </div>
                <div className="ml-auto text-xs font-medium">
                  {new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN" }).format(p.price)}
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export function CreateInvoicePage() {
  const { id: editId } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { storeId, currentStore } = useCurrentStore();
  const isEdit = !!editId;

  const { data: invoiceSettings } = useQuery<InvoiceSettings>({
    queryKey: ["invoice-settings", storeId],
    queryFn: async () => {
      const { data } = await api.get("/api/settings");
      return data.invoiceSettings;
    },
    enabled: !!storeId,
  });

  // Invoice fields
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [clientAddress, setClientAddress] = useState("");
  const [items, setItems] = useState<ItemRow[]>([newItem()]);
  const [taxRate, setTaxRate] = useState(0);
  const [notes, setNotes] = useState("");
  const [terms, setTerms] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [issueDate, setIssueDate] = useState(new Date().toISOString().split("T")[0]);
  const [status, setStatus] = useState<Invoice["status"]>("draft");
  const [templateId, setTemplateId] = useState<InvoiceTemplate>("classic");
  const [currency, setCurrency] = useState(currentStore?.currency || "NGN");

  // Load existing invoice for editing
  const { isLoading: isLoadingExisting, data: existingInvoice } = useQuery<Invoice>({
    queryKey: ["invoice", editId],
    queryFn: async () => {
      const { data } = await api.get(`/api/invoices/${editId}`);
      return data;
    },
    enabled: isEdit,
  });

  // Populate form when existing invoice loads
  useEffect(() => {
    if (existingInvoice) {
      setInvoiceNumber(existingInvoice.invoiceNumber);
      setClientName(existingInvoice.client.name);
      setClientEmail(existingInvoice.client.email || "");
      setClientPhone(existingInvoice.client.phone || "");
      setClientAddress(existingInvoice.client.address || "");
      setItems(existingInvoice.items.map((i) => ({ ...i, _key: Math.random().toString(36).slice(2) })));
      setTaxRate(existingInvoice.taxRate);
      setNotes(existingInvoice.notes || "");
      setTerms(existingInvoice.terms || "");
      setDueDate(existingInvoice.dueDate ? existingInvoice.dueDate.split("T")[0] : "");
      setIssueDate(existingInvoice.issueDate ? existingInvoice.issueDate.split("T")[0] : new Date().toISOString().split("T")[0]);
      setStatus(existingInvoice.status);
      setTemplateId(existingInvoice.templateId);
      setCurrency(existingInvoice.currency);
    }
  }, [existingInvoice]);

  // Apply defaults from invoice settings
  useEffect(() => {
    if (invoiceSettings && !isEdit) {
      setTaxRate(invoiceSettings.defaultTaxRate || 0);
      setNotes(invoiceSettings.defaultNotes || "");
      setTerms(invoiceSettings.defaultTerms || "");
      setTemplateId(invoiceSettings.defaultTemplate || "classic");
      const days = invoiceSettings.defaultPaymentTerms || 14;
      const dd = new Date();
      dd.setDate(dd.getDate() + days);
      setDueDate(dd.toISOString().split("T")[0]);
    }
  }, [invoiceSettings, isEdit]);

  // Computed totals
  const subTotal = items.reduce((s, i) => s + lineTotal(i), 0);
  const taxAmount = subTotal * (taxRate / 100);
  const total = subTotal + taxAmount;

  const { mutate: saveInvoice, isPending: isSaving } = useMutation({
    mutationFn: async () => {
      const payload = {
        invoiceNumber: invoiceNumber || undefined,
        client: { name: clientName, email: clientEmail, phone: clientPhone, address: clientAddress },
        items: items.map(({ _key, ...rest }) => rest),
        taxRate,
        notes,
        terms,
        dueDate: dueDate || undefined,
        issueDate,
        status,
        currency,
        templateId,
      };

      if (isEdit) {
        const { data } = await api.patch(`/api/invoices/${editId}`, payload);
        return data;
      } else {
        const { data } = await api.post("/api/invoices", payload);
        return data;
      }
    },
    onSuccess: (data: Invoice) => {
      // Seed the individual invoice cache immediately so the view page
      // renders the updated template without waiting for a refetch.
      queryClient.setQueryData(["invoice", data._id], data);
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      queryClient.invalidateQueries({ queryKey: ["invoice-analytics"] });
      toast.success(isEdit ? "Invoice updated" : "Invoice created");
      navigate(`/dashboard/invoices/${data._id}`);
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || "Failed to save invoice");
    },
  });

  const addItem = () => setItems((prev) => [...prev, newItem()]);
  const removeItem = (key: string) =>
    setItems((prev) => prev.filter((i) => i._key !== key));
  const updateItem = (key: string, field: keyof ItemRow, value: unknown) =>
    setItems((prev) =>
      prev.map((i) => (i._key === key ? { ...i, [field]: value } : i))
    );

  const onProductSelect = (p: ProductSuggestion) => {
    setItems((prev) => [
      ...prev,
      { _key: Math.random().toString(36).slice(2), itemName: p.name, description: p.description, unitPrice: p.price, quantity: 1, discount: 0, productId: p._id },
    ]);
  };

  if (isEdit && isLoadingExisting) {
    return <PageLoader text="Loading invoice" />;
  }

  return (
    <div className="space-y-6">
      <DashboardHead pageName={isEdit ? "Edit Invoice" : "New Invoice"} />
      <PageHeader title={isEdit ? "Edit Invoice" : "New Invoice"} />

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Main form */}
        <div className="xl:col-span-2 space-y-6">
          {/* Invoice Meta */}
          <Card className="p-5 space-y-4">
            <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Invoice Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Invoice Number</Label>
                <Input
                  placeholder="e.g. INV-001 (auto-generated if blank)"
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Status</Label>
                <Select value={status} onValueChange={(v) => setStatus(v as Invoice["status"])}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {["draft", "sent", "partial", "paid", "overdue", "cancelled"].map((s) => (
                      <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Issue Date</Label>
                <Input
                  type="date"
                  value={issueDate}
                  onChange={(e) => setIssueDate(e.target.value)}
                  className="mt-1"
                  aria-label="Issue date"
                />
              </div>
              <div>
                <Label className="text-xs">Due Date</Label>
                <Input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="mt-1"
                  aria-label="Due date"
                />
              </div>
              <div>
                <Label className="text-xs">Currency</Label>
                <Input
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                  placeholder="NGN"
                  className="mt-1"
                  maxLength={3}
                />
              </div>
              <div>
                <Label className="text-xs">Template</Label>
                <Select value={templateId} onValueChange={(v) => setTemplateId(v as InvoiceTemplate)}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TEMPLATES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </Card>

          {/* Customer */}
          <Card className="p-5 space-y-4">
            <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Customer Information</h3>
            {/* Customer search */}
            <CustomerAutocomplete
              storeId={storeId}
              onSelect={(c) => {
                setClientName(c.name);
                setClientEmail(c.email || "");
                setClientPhone(c.phone || "");
                setClientAddress(c.address || "");
              }}
            />
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-xs">Customer Name <span className="text-red-500">*</span></Label>
                <Input value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="Full name or business name" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input type="email" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} placeholder="customer@example.com" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Phone</Label>
                <Input value={clientPhone} onChange={(e) => setClientPhone(e.target.value)} placeholder="+234 xxx xxx xxxx" className="mt-1" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Address</Label>
                <Input value={clientAddress} onChange={(e) => setClientAddress(e.target.value)} placeholder="Street, city, state" className="mt-1" />
              </div>
            </div>
          </Card>

          {/* Items */}
          <Card className="p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Line Items</h3>
            </div>

            {/* Product autocomplete */}
            <ProductAutocomplete onSelect={onProductSelect} storeId={storeId} />

            {/* Items table */}
            <div className="space-y-2">
              {/* Header */}
              <div className="hidden sm:grid grid-cols-[2fr_80px_120px_80px_100px_36px] gap-2 text-xs text-muted-foreground font-medium px-1">
                <span>Item</span>
                <span className="text-right">Qty</span>
                <span className="text-right">Unit Price</span>
                <span className="text-right">Disc.%</span>
                <span className="text-right">Total</span>
                <span></span>
              </div>
              {items.map((item, index) => (
                <div
                  key={item._key}
                  className="grid grid-cols-1 sm:grid-cols-[2fr_80px_120px_80px_100px_36px] gap-2 items-start rounded-lg border border-border p-3 sm:border-0 sm:p-0 sm:rounded-none"
                >
                  <div>
                    <Label className="mb-1 block text-xs sm:sr-only">
                      Item name{items.length > 1 ? ` ${index + 1}` : ""}
                    </Label>
                    <Input
                      placeholder="Item name"
                      value={item.itemName}
                      onChange={(e) => updateItem(item._key, "itemName", e.target.value)}
                      className="mt-1 h-8 text-sm"
                    />
                    <Input
                      placeholder="Description (optional)"
                      value={item.description || ""}
                      onChange={(e) => updateItem(item._key, "description", e.target.value)}
                      className="h-7 text-xs mt-1 text-muted-foreground"
                    />
                  </div>
                  <div>
                    <Label className="mb-1 block text-xs sm:sr-only">Quantity</Label>
                    <Input
                      type="number"
                      min={1}
                      placeholder="Qty"
                      value={item.quantity}
                      onChange={(e) => updateItem(item._key, "quantity", Number(e.target.value))}
                      className="mt-1 h-8 text-sm text-right sm:mt-0"
                    />
                  </div>
                  <div>
                    <Label className="mb-1 block text-xs sm:sr-only">Unit price</Label>
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      placeholder="0.00"
                      value={item.unitPrice}
                      onChange={(e) => updateItem(item._key, "unitPrice", Number(e.target.value))}
                      className="mt-1 h-8 text-sm text-right sm:mt-0"
                    />
                  </div>
                  <div>
                    <Label className="mb-1 block text-xs sm:sr-only">Discount %</Label>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      placeholder="0"
                      value={item.discount || 0}
                      onChange={(e) => updateItem(item._key, "discount", Number(e.target.value))}
                      className="mt-1 h-8 text-sm text-right sm:mt-0"
                    />
                  </div>
                  <div className="flex items-center justify-between gap-2 sm:block">
                    <Label className="mb-1 block text-xs text-muted-foreground sm:sr-only">Line total</Label>
                    <div className="h-8 flex items-center justify-end text-sm font-medium pr-1 sm:justify-end">
                      {new Intl.NumberFormat("en-NG", { style: "currency", currency }).format(lineTotal(item))}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 justify-self-end text-muted-foreground hover:text-destructive sm:justify-self-auto"
                    onClick={() => removeItem(item._key)}
                    disabled={items.length === 1}
                    type="button"
                    aria-label="Remove line item"
                  >
                    <RiDeleteBin6Line className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>

            <Button variant="outline" size="sm" onClick={addItem} type="button">
              <RiAddLine className="h-4 w-4 mr-1.5" />
              Add item
            </Button>
          </Card>

          {/* Notes */}
          <Card className="p-5 space-y-4">
            <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Notes & Terms</h3>
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any additional information for the client…"
                className="mt-1 resize-none"
                rows={3}
              />
            </div>
            <div>
              <Label className="text-xs">Terms & Conditions</Label>
              <Textarea
                value={terms}
                onChange={(e) => setTerms(e.target.value)}
                placeholder="Payment terms, late fees, etc…"
                className="mt-1 resize-none"
                rows={3}
              />
            </div>
          </Card>
        </div>

        {/* Sidebar: summary */}
        <div className="space-y-4">
          <Card className="p-5 space-y-4 sticky top-4 md:top-12">
            <h3 className="font-medium">Summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Subtotal</span>
                <span>{new Intl.NumberFormat("en-NG", { style: "currency", currency }).format(subTotal)}</span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground shrink-0">Tax rate</span>
                <div className="flex items-center gap-1">
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    step="0.1"
                    placeholder="0"
                    value={taxRate}
                    onChange={(e) => setTaxRate(Number(e.target.value))}
                    className="w-16 h-7 text-xs text-right"
                    aria-label="Tax rate percentage"
                  />
                  <span className="text-xs">%</span>
                </div>
                <span>{new Intl.NumberFormat("en-NG", { style: "currency", currency }).format(taxAmount)}</span>
              </div>
              <div className="flex justify-between border-t pt-2 font-bold text-base">
                <span>Total</span>
                <span>{new Intl.NumberFormat("en-NG", { style: "currency", currency }).format(total)}</span>
              </div>
            </div>

            <div className="space-y-2 pt-2">
              <Button
                className="w-full"
                onClick={() => saveInvoice()}
                disabled={isSaving || !clientName.trim() || !items[0].itemName.trim()}
              >
                {isSaving ? "Saving…" : isEdit ? "Update Invoice" : "Create Invoice"}
              </Button>
              <Button variant="outline" className="w-full" onClick={() => navigate(-1)}>
                Cancel
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
